import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import {
    Box, Button, Card, CardContent, Chip, CircularProgress, Divider, IconButton, Link,
    Paper, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined';
import LanguageIcon from '@mui/icons-material/Language';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useTranslation } from '../hooks/useTranslation';
import { getDemoSites } from '../Utils/demoSitesApi';
import { SERVICES } from '../Utils/ticketApi';

const API_URL = process.env.REACT_APP_BACKEND_API_URL || 'http://localhost:8080';

function DemoAccess({ access }) {
    const t = useTranslation('Demo.json');
    const [showPassword, setShowPassword] = useState(false);

    const copy = (value) => {
        if (value) navigator.clipboard?.writeText(value).catch(() => {});
    };

    return (
        <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'action.hover' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{access.label}</Typography>
                {access.url && (
                    <Button
                        size="small"
                        endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                        href={access.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ color: '#FF7233' }}
                    >
                        {t('demo.access.open')}
                    </Button>
                )}
            </Stack>
            {access.login && (
                <Stack direction="row" spacing={0.5} alignItems="center">
                    <Typography variant="caption" color="text.secondary">
                        {t('demo.access.login')} : {access.login}
                    </Typography>
                    <Tooltip title={t('demo.access.copy')}>
                        <IconButton size="small" onClick={() => copy(access.login)}>
                            <ContentCopyIcon sx={{ fontSize: 13 }} />
                        </IconButton>
                    </Tooltip>
                </Stack>
            )}
            {access.password && (
                <Stack direction="row" spacing={0.5} alignItems="center">
                    <Typography variant="caption" color="text.secondary">
                        {t('demo.access.password')} : {showPassword ? access.password : '••••••••'}
                    </Typography>
                    <IconButton size="small" onClick={() => setShowPassword((v) => !v)}>
                        {showPassword ? <VisibilityOffIcon sx={{ fontSize: 13 }} /> : <VisibilityIcon sx={{ fontSize: 13 }} />}
                    </IconButton>
                    <Tooltip title={t('demo.access.copy')}>
                        <IconButton size="small" onClick={() => copy(access.password)}>
                            <ContentCopyIcon sx={{ fontSize: 13 }} />
                        </IconButton>
                    </Tooltip>
                </Stack>
            )}
        </Box>
    );
}

export default function DemoPage() {
    const navigate = useNavigate();
    const t = useTranslation('Demo.json');

    const [sites, setSites] = useState([]);
    const [loading, setLoading] = useState(true);
    const [category, setCategory] = useState('');
    const [search, setSearch] = useState('');

    useEffect(() => {
        getDemoSites()
            .then(setSites)
            .catch(() => setSites([]))
            .finally(() => setLoading(false));
    }, []);

    const categories = useMemo(
        () => Array.from(new Set(sites.map((s) => s.category).filter(Boolean))).sort(),
        [sites]
    );

    const filteredSites = useMemo(() => {
        return sites.filter((s) => {
            if (category && s.category !== category) return false;
            if (search && !s.name.toLowerCase().includes(search.toLowerCase())) return false;
            return true;
        });
    }, [sites, category, search]);

    const contactCommercial = () => {
        navigate('/tickets', { state: { service: SERVICES.COMMERCIAL, openCreate: true } });
    };

    return (
        <Box sx={{ width: '100%', p: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 3 }} flexWrap="wrap" spacing={2}>
                <Stack>
                    <Typography variant="h4" sx={{ fontWeight: 600 }}>{t('demo.title')}</Typography>
                    <Typography color="text.secondary">{t('demo.subtitle')}</Typography>
                </Stack>
                <Button
                    variant="contained"
                    startIcon={<SupportAgentOutlinedIcon />}
                    onClick={contactCommercial}
                    sx={{ bgcolor: '#FF7233', '&:hover': { bgcolor: '#e5601f' } }}
                >
                    {t('demo.contact_commercial')}
                </Button>
            </Stack>

            <Stack direction="row" spacing={1} sx={{ mb: 3 }} flexWrap="wrap" useFlexGap alignItems="center">
                <Chip
                    label={t('demo.filter.all')}
                    onClick={() => setCategory('')}
                    color={category === '' ? 'primary' : 'default'}
                    sx={category === '' ? { bgcolor: '#FF7233' } : {}}
                />
                {categories.map((cat) => (
                    <Chip
                        key={cat}
                        label={cat}
                        onClick={() => setCategory(cat)}
                        color={category === cat ? 'primary' : 'default'}
                        sx={category === cat ? { bgcolor: '#FF7233' } : {}}
                    />
                ))}
                <TextField
                    size="small"
                    placeholder="Rechercher..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    sx={{ ml: 'auto', minWidth: 200 }}
                />
            </Stack>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
                    <CircularProgress sx={{ color: '#FF7233' }} />
                </Box>
            ) : filteredSites.length === 0 ? (
                <Paper elevation={1} sx={{ borderRadius: 3, p: 6, textAlign: 'center' }}>
                    <Typography color="text.secondary">{t('demo.empty')}</Typography>
                </Paper>
            ) : (
                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                    gap: 3,
                }}>
                    {filteredSites.map((site) => (
                        <Card key={site.id} elevation={1} sx={{ borderRadius: 3, overflow: 'hidden' }}>
                            <Box sx={{
                                height: 140,
                                bgcolor: 'action.hover',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                overflow: 'hidden',
                            }}>
                                {site.image ? (
                                    <Box
                                        component="img"
                                        src={`${API_URL}${site.image}`}
                                        alt={site.name}
                                        sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                    />
                                ) : (
                                    <LanguageIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
                                )}
                            </Box>
                            <CardContent>
                                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                                    <Typography variant="h6" sx={{ fontWeight: 600 }} noWrap>{site.name}</Typography>
                                </Stack>
                                {(site.category || site.subcategory) && (
                                    <Stack direction="row" spacing={0.5} sx={{ mb: 1 }}>
                                        {site.category && <Chip size="small" label={site.category} />}
                                        {site.subcategory && <Chip size="small" variant="outlined" label={site.subcategory} />}
                                    </Stack>
                                )}
                                <Link
                                    href={site.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.85rem', mb: 1.5 }}
                                    noWrap
                                >
                                    {site.url}
                                    <OpenInNewIcon sx={{ fontSize: 14 }} />
                                </Link>

                                {(site.Accesses || []).length > 0 && (
                                    <>
                                        <Divider sx={{ mb: 1.5 }} />
                                        <Stack spacing={1}>
                                            {site.Accesses.map((access) => (
                                                <DemoAccess key={access.id} access={access} />
                                            ))}
                                        </Stack>
                                    </>
                                )}
                            </CardContent>
                        </Card>
                    ))}
                </Box>
            )}
        </Box>
    );
}
